import { get, post, put, del } from '../api';

// ─── Formatting helpers (unchanged) ─────────────────────────────

export function sentenceCase(str) {
  return str
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function isAbbreviation(word) {
  const dotPattern = /^([a-zA-Z]\.){2,}$/;
  const stripped = word.replace(/[.,/\\]/g, '');
  if (dotPattern.test(word)) return true;
  if (/^[a-zA-Z]{2,4}$/.test(stripped) && stripped.toLowerCase() === stripped && /[.\/\\]/.test(word)) return true; // eslint-disable-line no-useless-escape
  return false;
}

function formatWord(word) {
  const clean = word.replace(/[.,/\\]/g, '');
  if (!clean) return '';
  if (isAbbreviation(word)) {
    return clean.toUpperCase();
  }
  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
}

export function formatPart(str) {
  return str
    .trim()
    .split(/\s+/)
    .map((word) => formatWord(word))
    .filter(Boolean)
    .join('');
}

export function formatTestSiteName(clientName, city) {
  return `${formatPart(clientName)} - ${formatPart(city)}`;
}

export function formatClientNameForSave(str) {
  return str
    .trim()
    .split(/\s+/)
    .map((word) => {
      const clean = word.replace(/[.,/\\]/g, '');
      if (/^[A-Z]{2,4}$/.test(clean)) {
        return clean;
      }
      if (isAbbreviation(word)) {
        return clean.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function isProfileComplete(client) {
  return !!(
    client.contactPerson &&
    client.addressLine &&
    client.postCode &&
    client.country
  );
}

export function isObjectIncomplete(obj) {
  return getObjectMissing(obj).length > 0;
}

// Return the list of human-readable field labels that are missing on
// the object. Used by the warning row on ObjectDetail so the user sees
// exactly what is still to be filled in.
export function getObjectMissing(obj) {
  const missing = [];
  if (!obj) { missing.push('Object Type', 'Object ID'); return missing; }
  if (!obj.type)      missing.push('Object Type');
  if (!obj.objectTag) missing.push('Object ID');
  const ds = obj.designSpec || {};
  // Design
  if (!ds.objectSpec)     missing.push('Object Specification');
  if (!ds.attributeA)     missing.push('Attribute A');
  if (!ds.attributeAType) missing.push('Attribute A Type');
  if (!ds.attributeB)     missing.push('Attribute B');
  if (!ds.attributeBType) missing.push('Attribute B Type');
  if (!ds.productStored && !ds.productStoredRadio) missing.push('Product Stored');
  // Sizing
  if (!ds.dimensionA)             missing.push('Dimension A');
  if (!ds.dimASize)               missing.push('Dimension A Size');
  if (!ds.dimensionB)             missing.push('Dimension B');
  if (!ds.dimBSize)               missing.push('Dimension B Size');
  if (!ds.objectCloselyResembles) missing.push('Object Closely Resembles');
  // Construction
  if (!ds.buildingStandard) missing.push('Building Standard');
  if (!ds.material1)        missing.push('Material 1');
  if (!ds.material1Type)    missing.push('Material 1 Type');
  if (!ds.material2)        missing.push('Material 2');
  if (!ds.material2Type)    missing.push('Material 2 Type');
  if (!ds.insulation)       missing.push('Insulation');
  if (ds.manholeNorthAngle === undefined || ds.manholeNorthAngle === null || ds.manholeNorthAngle === '') {
    missing.push('Manhole Angle from True North');
  }
  // Miscellaneous
  if (!ds.heatingElement)    missing.push('Heating Element');
  if (!ds.structuralSupport) missing.push('Structural Support');
  if (!ds.designPressure && !ds.designPressureRadio) missing.push('Design Pressure');
  if (!ds.designTemp     && !ds.designTempRadio)     missing.push('Design Temperature');
  if (!ds.yearOfConstruction) missing.push('Year of Construction');
  if (!ds.previousTIYear)     missing.push('Previous TI Inspection Year');
  return missing;
}

// Return the list of missing Assessment Information field labels.
export function getAssessmentMissing(gi) {
  const missing = [];
  if (!gi) gi = {};
  if (!gi.inspectionStandard) missing.push('Inspection Standard');
  if (!gi.inspectionNumber)   missing.push('Project Number');
  if (!gi.startDate)          missing.push('Start Date');
  if (!gi.endDate)            missing.push('End Date');
  if (!gi.accountManager)     missing.push('Account Manager');
  if (!gi.leadNDTOperator)    missing.push('Lead NDT Operator');
  if (gi.involvementL3Inspector && !gi.l3Inspector) missing.push('L3 Inspector');
  return missing;
}

// ─── snake_case ↔ camelCase mapping ────────────────────────────

function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function camelToSnake(str) {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

// Map API response (snake_case) to frontend (camelCase) for top-level fields
function apiToFrontend(record) {
  if (!record) return record;
  return {
    id: record.id,
    clientName: record.client_name,
    city: record.city,
    name: record.name,
    nameOverride: record.name_override,
    contactPerson: record.contact_person,
    addressLine: record.address_line,
    postCode: record.post_code,
    country: record.country,
    clientId: record.client_id,
    objectId: record.object_id,
    type: record.type,
    objectTag: record.object_tag,
    designSpec: record.design_spec,
    generalInfo: record.general_info,
    settings: record.settings,
    googleDrivePath: record.google_drive_path,
    threeDInspectUrl: record.three_d_inspect_url,
    capturesObjectType: record.captures_object_type,
    capturesComposition: record.captures_composition,
    description: record.description,
    status: record.status,
    formData: record.form_data,
    items: record.items,
    assignedTo: record.assigned_to,
    // User fields
    email: record.email,
    displayName: record.display_name,
    photoURL: record.photo_url,
    roles: record.roles,
    uid: record.id, // map id to uid for user compatibility
    createdAt: record.created_at,
    // Pass through anything else
    ...Object.fromEntries(
      Object.entries(record)
        .filter(([k]) => !['client_name', 'city', 'name', 'name_override', 'contact_person',
          'address_line', 'post_code', 'country', 'client_id', 'object_id', 'type',
          'object_tag', 'design_spec', 'general_info', 'settings', 'google_drive_path',
          'three_d_inspect_url', 'captures_object_type', 'captures_composition',
          'description', 'status', 'form_data', 'items', 'assigned_to', 'email',
          'display_name', 'photo_url', 'roles', 'created_at', 'id'].includes(k))
        .map(([k, v]) => [snakeToCamel(k), v])
    ),
  };
}

// Map a list of API records to frontend format
function apiListToFrontend(records) {
  return records.map(apiToFrontend);
}

// ─── Polling helper (replaces onSnapshot) ──────────────────────
// Returns an unsubscribe function just like onSnapshot did
function createPoller(fetchFn, callback, interval = 15000) {
  let active = true;

  async function poll() {
    if (!active) return;
    try {
      const data = await fetchFn();
      if (active) callback(data);
    } catch (err) {
      console.error('Polling error:', err);
    }
  }

  // Initial fetch
  poll();

  // Set up interval
  const id = setInterval(poll, interval);

  // Return unsubscribe function
  return () => {
    active = false;
    clearInterval(id);
  };
}

// ─── Shared DDL cache ──────────────────────────────────────────────
//
// Every dropdown in the app subscribes via one of the subscribeToDDL*
// helpers below. Without a shared cache each subscriber would issue its
// own GET /ddl, and the first paint of every <select> would wait on the
// network — which is why dropdowns rendered blank/black for a moment
// before populating.
//
// _ddlCache is primed from localStorage on module load, so the very
// first render after navigation has options ready synchronously.
// _ddlInFlight de-duplicates concurrent requests across subscribers
// (each poll tick now makes one network call, regardless of subscriber
// count).

const DDL_CACHE_KEY = 'tib_ddl_cache_v1';
let _ddlCache    = null;   // last known apiListToFrontend(/ddl) result
let _ddlInFlight = null;

(function _primeDDLFromStorage() {
  try {
    const raw = localStorage.getItem(DDL_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) _ddlCache = parsed;
    }
  } catch {
    // ignore corrupt cache
  }
})();

async function fetchDDLShared() {
  if (_ddlInFlight) return _ddlInFlight;
  _ddlInFlight = (async () => {
    try {
      const data = await get('/ddl');
      const cols = apiListToFrontend(data);
      _ddlCache = cols;
      try { localStorage.setItem(DDL_CACHE_KEY, JSON.stringify(cols)); } catch {}
      return cols;
    } finally {
      _ddlInFlight = null;
    }
  })();
  return _ddlInFlight;
}

function createDDLPoller(deriveFn, callback, interval = 15000) {
  let active = true;

  // Synchronous emit from cache so the first paint of any <select> has
  // options ready without waiting on the network.
  if (_ddlCache) {
    try { callback(deriveFn(_ddlCache)); }
    catch (err) { console.error('DDL derive error (cache):', err); }
  }

  async function poll() {
    if (!active) return;
    try {
      const cols = await fetchDDLShared();
      if (active) callback(deriveFn(cols));
    } catch (err) {
      console.error('DDL polling error:', err);
    }
  }

  poll();
  const id = setInterval(poll, interval);
  return () => { active = false; clearInterval(id); };
}

// ─── Clients (async) ────────────────────────────────────────────

export async function getClients() {
  const data = await get('/clients');
  return apiListToFrontend(data);
}

export async function getClientById(id) {
  try {
    const data = await get(`/clients/${id}`);
    return apiToFrontend(data);
  } catch {
    return null;
  }
}

export async function addClient(clientName, city, nameOverride) {
  const autoName = formatTestSiteName(clientName, city);
  const data = await post('/clients', {
    client_name: formatClientNameForSave(clientName),
    city: sentenceCase(city),
    name: nameOverride || autoName,
    name_override: nameOverride || null,
    contact_person: '',
    address_line: '',
    post_code: '',
    country: '',
  });
  return apiToFrontend(data);
}

export async function updateClient(id, updates) {
  // Convert camelCase keys to snake_case for the API
  const apiUpdates = {};
  for (const [key, value] of Object.entries(updates)) {
    apiUpdates[camelToSnake(key)] = value;
  }
  await put(`/clients/${id}`, apiUpdates);
}

export async function canDeleteClient(id) {
  const objects = await getObjectsByClient(id);
  return objects.length === 0;
}

export async function deleteClient(id) {
  const deletable = await canDeleteClient(id);
  if (!deletable) return false;
  await del(`/clients/${id}`);
  return true;
}

// ─── Objects (async) ────────────────────────────────────────────

export async function getObjectsByClient(clientId) {
  const data = await get(`/objects?client_id=${clientId}`);
  return apiListToFrontend(data);
}

// Active AND archived objects of a test site — for the "Select from Existing
// Objects" step that guards against re-creating an object already on file.
export async function getAllObjectsByClient(clientId) {
  const data = await get(`/objects?client_id=${clientId}&include_archived=true`);
  return apiListToFrontend(data);
}

// New active object for a new project on an object already on file: copies
// the object-level info (type, ID, design spec, captures fields) but not the
// project-level parts (assessment info, settings, inspections, Drive path,
// 3D-Inspect URL, photo). Same type + ID means it files under the existing
// object in Archived Projects once archived.
export async function addObjectFromExisting(src) {
  const data = await post('/objects', {
    client_id: src.clientId,
    type: src.type || '',
    object_tag: src.objectTag || '',
    design_spec: src.designSpec ?? null,
    captures_object_type: src.capturesObjectType || '',
    captures_composition: src.capturesComposition || '',
    settings: { reportingPeriod: '2 Weeks' },
  });
  return apiToFrontend(data);
}

export async function getObjectById(id) {
  try {
    const data = await get(`/objects/${id}`);
    return apiToFrontend(data);
  } catch {
    return null;
  }
}

export async function addObject(clientId, type, objectTag = '') {
  const data = await post('/objects', {
    client_id: clientId,
    type,
    object_tag: objectTag,
    design_spec: null,
    settings: { reportingPeriod: '2 Weeks' },
  });
  return apiToFrontend(data);
}

export async function updateObjectDesign(objectId, designData) {
  await put(`/objects/${objectId}`, { design_spec: designData });
}

export async function updateObjectGeneralInfo(objectId, generalInfo) {
  await put(`/objects/${objectId}`, { general_info: generalInfo });
}

export async function updateObjectSettings(objectId, settings) {
  await put(`/objects/${objectId}`, { settings });
}

export function getObjectPhotoUrl(objectId) {
  return `/api/objects/${objectId}/photo`;
}

export async function uploadObjectPhoto(objectId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/objects/${objectId}/photo`, {
    method: 'PUT',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Photo upload failed');
  }
}

export async function deleteObjectPhoto(objectId) {
  const res = await fetch(`/api/objects/${objectId}/photo`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Photo delete failed');
}

export async function updateObjectInfo(objectId, { type, objectTag, googleDrivePath, threeDInspectUrl }) {
  await put(`/objects/${objectId}`, {
    type,
    object_tag: objectTag,
    google_drive_path: googleDrivePath || '',
    three_d_inspect_url: threeDInspectUrl || '',
  });
}

export async function updateObjectCaptures(objectId, { capturesObjectType, capturesComposition }) {
  await put(`/objects/${objectId}`, {
    captures_object_type: capturesObjectType,
    captures_composition: capturesComposition,
  });
}

export function subscribeToObjectById(objectId, callback) {
  return createPoller(
    async () => {
      const data = await get(`/objects/${objectId}`);
      return apiToFrontend(data);
    },
    callback,
  );
}

export async function deleteObject(id) {
  await del(`/objects/${id}`);
}

// Silently archive an object (hidden from active app, kept for Archived
// Projects). Backend 409s if it has no inspections or any isn't at Delivery.
// `force` is an admin-only override: archive despite non-Delivery inspections
// (the backend first sets those inspections to Delivery).
export async function archiveObject(id, force = false) {
  return apiToFrontend(await post(`/objects/${id}/archive`, { force }));
}

// Archived objects for the Archived Projects tree (raw shape from the backend:
// { id, client_id, testSite, type, object_tag, startDate, projectNumber,
//   archivedAt, hasProject }).
export async function getArchivedObjects() {
  try { const r = await get('/objects/archived'); return r?.objects || []; }
  catch { return []; }
}

// Permanently delete an archived object (cascades its inspections/images/links).
export async function deleteArchivedObject(id) {
  return del(`/objects/${id}/archived`);
}

// Remove an archived object's project data (inspections + assessment) but keep
// the object shell under Archived Projects.
export async function stripArchivedProject(id) {
  return post(`/objects/${id}/strip-project`, {});
}

// ─── Inspections (async) ────────────────────────────────────────

export async function getInspectionsByObject(objectId) {
  const data = await get(`/inspections?object_id=${objectId}`);
  return apiListToFrontend(data);
}

export async function getInspectionById(id) {
  try {
    const data = await get(`/inspections/${id}`);
    return apiToFrontend(data);
  } catch {
    return null;
  }
}

export async function addInspection(objectId, name, description, type) {
  const data = await post('/inspections', {
    object_id: objectId,
    name,
    description,
    type,
    status: 'Planning',
  });
  return apiToFrontend(data);
}

export async function deleteInspection(id) {
  await del(`/inspections/${id}`);
}

export async function updateInspectionData(id, data) {
  // Convert camelCase keys to snake_case
  const apiData = {};
  for (const [key, value] of Object.entries(data)) {
    apiData[camelToSnake(key)] = value;
  }
  await put(`/inspections/${id}`, apiData);
}

// ─── Real-time listeners (polling replacements) ─────────────────

export function subscribeToClients(callback) {
  return createPoller(
    async () => {
      const data = await get('/clients');
      return apiListToFrontend(data);
    },
    callback,
  );
}

export function subscribeToAllObjects(callback) {
  return createPoller(
    async () => {
      const data = await get('/objects');
      return apiListToFrontend(data);
    },
    callback,
  );
}

export function subscribeToObjectsByClient(clientId, callback) {
  return createPoller(
    async () => {
      const data = await get(`/objects?client_id=${clientId}`);
      return apiListToFrontend(data);
    },
    callback,
  );
}

export function subscribeToInspectionsByObject(objectId, callback) {
  return createPoller(
    async () => {
      const data = await get(`/inspections?object_id=${objectId}`);
      return apiListToFrontend(data);
    },
    callback,
  );
}

export function subscribeToAllInspections(callback) {
  return createPoller(
    async () => {
      const data = await get('/inspections/all');
      return apiListToFrontend(data);
    },
    callback,
  );
}

// ─── DDL Collections ────────────────────────────────────────────

export function sortDDLItems(items) {
  const pinnedLower = ['not applicable', 'not available'];
  const isPinned = (it) => pinnedLower.includes(it.label.trim().toLowerCase());
  const normal = items
    .filter((it) => !isPinned(it))
    .sort((a, b) => a.label.localeCompare(b.label));
  const bottom = items.filter((it) => isPinned(it));
  return [...normal, ...bottom];
}

export function getCollectionDepthTag(items) {
  if (!items || items.length === 0) return 'Primary';
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      for (const child of item.children) {
        if (child.children && child.children.length > 0) {
          return 'Tertiary';
        }
      }
      return 'Secondary';
    }
  }
  return 'Primary';
}

export function subscribeToDDLCollections(callback) {
  return createDDLPoller((cols) => cols, callback);
}

// One-shot fetch — used by DDLDetail to refresh immediately after a mutation
// without waiting for the next 15s poll tick.
export async function getDDLCollections() {
  const data = await get('/ddl');
  return apiListToFrontend(data);
}

export function subscribeToDDLByDropdownId(dropdownId, callback) {
  const pinnedLower = ['not applicable', 'not available'];
  const isPinned = (it) => pinnedLower.includes(it.label.trim().toLowerCase());

  return createDDLPoller((cols) => {
    const matched = cols.find((c) => {
      if (!c.assignedTo) return false;
      if (Array.isArray(c.assignedTo)) return c.assignedTo.includes(dropdownId);
      return c.assignedTo === dropdownId;
    });
    if (!matched) return [];
    const items = matched.items || [];
    const sorted = [
      ...items.filter((it) => !isPinned(it)).sort((a, b) => a.label.localeCompare(b.label)),
      ...items.filter((it) => isPinned(it)),
    ];
    return sorted.map((it) => it.label);
  }, callback);
}

export function subscribeToDDLFullItemsByDropdownId(dropdownId, callback) {
  return createDDLPoller((cols) => {
    const matched = cols.find((c) => {
      if (!c.assignedTo) return false;
      if (Array.isArray(c.assignedTo)) return c.assignedTo.includes(dropdownId);
      return c.assignedTo === dropdownId;
    });
    return matched?.items || [];
  }, callback);
}

export function subscribeToDDLFullItemsByName(collectionName, callback) {
  return createDDLPoller((cols) => {
    const matched = cols.find((c) => c.name === collectionName);
    return matched?.items || [];
  }, callback);
}

// ─── Dropdown Registrations ──────────────────────────────────────

export function subscribeToDropdownRegistrations(callback) {
  return createPoller(
    async () => {
      return await get('/settings/dropdown-registrations');
    },
    callback,
  );
}

export async function saveDropdownRegistrations(configs) {
  await post('/settings/dropdown-registrations', { configs });
}

export async function addDDLCollection(name) {
  const data = await post('/ddl', { name: name.trim(), items: [], assigned_to: null });
  return apiToFrontend(data);
}

export async function updateDDLCollection(id, updates) {
  // Convert camelCase to snake_case for known fields
  const apiUpdates = {};
  for (const [key, value] of Object.entries(updates)) {
    if (key === 'assignedTo') {
      apiUpdates['assigned_to'] = value;
    } else {
      apiUpdates[key] = value;
    }
  }
  await put(`/ddl/${id}`, apiUpdates);
}

export async function deleteDDLCollection(id) {
  await del(`/ddl/${id}`);
}

// ─── Users ──────────────────────────────────────────────────────

export async function getHasAnyUsers() {
  // This is only used during auth flow which is now handled server-side
  // Return true since user creation is handled by the backend
  return true;
}

export async function getUserByUid(uid) {
  // Not used in new auth flow — backend handles this
  return null;
}

export async function getUserByEmail(email) {
  // Not used in new auth flow — backend handles this
  return null;
}

export async function migrateUserDoc(oldDocId, firebaseUser) {
  // Not needed in new auth flow
  return null;
}

export async function createUserDoc(firebaseUser, isFirstUser = false) {
  // Not used — backend creates users on Google auth
  return null;
}

export function subscribeToUserProfile(uid, callback) {
  return createPoller(
    async () => {
      const data = await get('/users/me');
      return {
        id: data.id,
        uid: data.id,
        email: data.email,
        displayName: data.display_name,
        photoURL: data.photo_url,
        roles: data.roles || [],
        createdAt: data.created_at,
      };
    },
    callback,
  );
}

// Reporter- and operator-role users (name + email + roles) — the inspection
// assignee picker and the dashboard's per-user project view.
// Available to any signed-in user, unlike the admin-only full user list.
export async function getReporterUsers() {
  try {
    const data = await get('/users/reporters');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function subscribeToUsers(callback) {
  return createPoller(
    async () => {
      const data = await get('/users');
      return data.map((u) => ({
        id: u.id,
        uid: u.id,
        email: u.email,
        displayName: u.display_name,
        photoURL: u.photo_url,
        roles: u.roles || [],
        createdAt: u.created_at,
      }));
    },
    callback,
  );
}

export async function updateUserRoles(uid, roles) {
  await put(`/users/${uid}/roles`, { roles });
}

// ─── App Roles ──────────────────────────────────────────────────

export function subscribeToAppRoles(callback) {
  return createPoller(
    async () => {
      return await get('/settings/roles');
    },
    callback,
  );
}

export async function addAppRole(roleName) {
  await post('/settings/roles', { role: roleName });
}

// ─── Delete user ─────────────────────────────────────────────────

export async function deleteUser(uid) {
  await del(`/users/${uid}`);
}

// ─── Database usage ──────────────────────────────────────────────

export function subscribeToDbUsage(callback) {
  // Not applicable in PostgreSQL — return placeholder
  callback({ usedGb: 0, limitGb: 100 });
  return () => {};
}

// ─── Inspection Captures ────────────────────────────────────────

export async function syncCaptures(inspectionId, drivePath, presetCodes, isPreset) {
  return post('/captures/sync', {
    inspection_id: inspectionId,
    drive_path: drivePath,
    preset_codes: presetCodes,
    is_preset: isPreset,
  });
}

export async function getSyncStatus(inspectionId) {
  return get(`/captures/sync-status/${inspectionId}`);
}

export async function deleteCaptures(inspectionId) {
  return del(`/captures/${inspectionId}`);
}

export async function getCaptures(inspectionId) {
  return get(`/captures/${inspectionId}`);
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export async function fetchCaptureImageBlob(captureId) {
  try {
    const res = await fetch(`${API_BASE_URL}/captures/${captureId}/image`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

export async function updateDbUsage(usedGb, limitGb) {
  // Not applicable in PostgreSQL
}

// ─── Appendix Images ───────────────────────────────────────────

export async function getAppendixImages(inspectionId) {
  return get(`/appendix/${inspectionId}`);
}

export async function uploadAppendixImage(inspectionId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE_URL}/appendix/${inspectionId}/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Upload failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchAppendixImageBlob(imageId) {
  try {
    const res = await fetch(`${API_BASE_URL}/appendix/${imageId}/image`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

export async function updateAppendixCaption(imageId, caption) {
  return put(`/appendix/${imageId}/caption`, { caption });
}

// Three-state PDF rendering width: 'normal' (12cm) | 'small' (9cm) |
// 'smaller' (2" box). Default is 'normal'.
export async function updateAppendixPdfSize(imageId, pdfSize) {
  return put(`/appendix/${imageId}/pdf-size`, { pdf_size: pdfSize });
}

export async function deleteAppendixImage(imageId) {
  return del(`/appendix/${imageId}`);
}

// ─── Sub-card Generated Reference Images ──────────────────────────
//
// Recipe-driven images composed from PNG layers on the NL server, scoped
// to a single UT sub-card. NEVER visible in the inspection-wide appendix.

export async function regenerateSubcardImages(inspectionId, subcardId, { component, measurement, layout }) {
  return post(`/subcard-images/${inspectionId}/${subcardId}/regenerate`, {
    component, measurement, layout: layout || {},
  });
}

export async function getSubcardImages(inspectionId, subcardId) {
  return get(`/subcard-images/${inspectionId}/${subcardId}`);
}

export async function fetchSubcardImageBlob(imageId) {
  try {
    const res = await fetch(`${API_BASE_URL}/subcard-images/blob/${imageId}`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

export async function calculateDbUsage() {
  // Not applicable in PostgreSQL
  return { usedGb: 0, limitGb: 100, totalDocs: 0 };
}

// ─── UT Measurements ─────────────────────────────────────────────

export async function getUTData(inspectionId) {
  return get(`/inspections/${inspectionId}/ut-data`);
}

export async function saveUTData(inspectionId, utData) {
  return put(`/inspections/${inspectionId}/ut-data`, { ut_data: utData });
}

// ─── Deformation analysis results ────────────────────────────────

// All saved deformation results for an inspection ([{ id, kind, standard, … }]).
export async function getDefoResults(inspectionId) {
  try {
    const r = await get(`/inspections/${inspectionId}/defo-results`);
    return Array.isArray(r) ? r : [];
  } catch { return []; }
}

// Delete the saved result (+ its images, via cascade) for one analysis.
// `standard` optional — when omitted, deletes every result for that kind.
export async function deleteDefoResultByKind(inspectionId, kind, standard) {
  const rows = await getDefoResults(inspectionId);
  const matches = rows.filter(
    (r) => r.kind === kind && (!standard || r.standard === standard),
  );
  for (const r of matches) {
    try { await del(`/defo-results/${r.id}`); } catch { /* best-effort */ }
  }
}

// ─── Customers (read-only mirror of TerraFlow Clients.json) ─────
// The backend serves an already-slimmed snake_case shape + cache metadata,
// so these pass the response through untouched (no apiToFrontend mapping).

// Fired after any assignment/mute change so badges + lists refresh instantly
// (the 60s pollers are just the safety net).
const CUSTOMERS_CHANGED = 'customers:changed';
function notifyCustomersChanged() {
  try { window.dispatchEvent(new CustomEvent(CUSTOMERS_CHANGED)); } catch { /* SSR/no-window */ }
}

// Poller + immediate refetch on the customers:changed event.
function createCustomerPoller(fetchFn, callback, interval = 60000) {
  const stop = createPoller(fetchFn, callback, interval);
  const onChange = () => { fetchFn().then(callback).catch(() => {}); };
  window.addEventListener(CUSTOMERS_CHANGED, onChange);
  return () => { stop(); window.removeEventListener(CUSTOMERS_CHANGED, onChange); };
}

export async function getCustomers() {
  // { items(enriched), updated_at, source_generated_at, stale, error, pending_count }
  return get('/customers');
}

export async function refreshCustomers() {
  return post('/customers/refresh', {});
}

export function subscribeToCustomers(callback) {
  return createCustomerPoller(async () => get('/customers'), callback, 60000);
}

export async function getCustomerById(id) {
  try { return await get(`/customers/${id}`); }
  catch { return null; }
}

export async function getCustomerAvailableSites(id) {
  const res = await get(`/customers/${id}/available-sites`);
  return res?.sites || [];
}

// Combined assignment update. `muted` toggles independently; pass
// setClient:true with clientId (UUID or null) to link/unassign a test site.
export async function updateCustomerAssignment(id, { muted, setClient, clientId } = {}) {
  const body = {};
  if (muted !== undefined) body.muted = muted;
  if (setClient) { body.set_client = true; body.client_id = clientId ?? null; }
  const res = await put(`/customers/${id}/assignment`, body);
  notifyCustomersChanged();
  return res;
}

export async function getCustomersPendingCount() {
  const res = await get('/customers/pending-count');
  return res?.count || 0;
}

export function subscribeToCustomersPendingCount(callback) {
  return createCustomerPoller(async () => {
    const res = await get('/customers/pending-count');
    return res?.count || 0;
  }, callback, 60000);
}

// ─── TerraFlow projects ─────────────────────────────────────────
// Mirror of projects owned by the external TerraFlow app. apiToFrontend
// already maps assigned_to→assignedTo and passes through the rest
// (terraflow_project_id→terraflowProjectId, object_ids→objectIds, etc.).

export async function getTerraFlowProjects() {
  const data = await get('/terraflow/projects');
  return apiListToFrontend(data);
}

export async function getTerraFlowProjectById(id) {
  try {
    return apiToFrontend(await get(`/terraflow/projects/${id}`));
  } catch {
    return null;
  }
}

export function subscribeToTerraFlowProjects(callback) {
  return createPoller(
    async () => apiListToFrontend(await get('/terraflow/projects')),
    callback,
  );
}

export function subscribeToTerraFlowProjectById(id, callback) {
  return createPoller(
    async () => apiToFrontend(await get(`/terraflow/projects/${id}`)),
    callback,
  );
}

// Admin-only on the backend. Returns { status, synced, configured }.
export async function syncTerraFlowProjects() {
  return post('/terraflow/sync', {});
}

// Resolve the TIB test site linked to a project's customer (via the
// customer↔test-site assignment). Backing the Customer card + Refresh button.
// Returns { project_number, client_code, customer_found, linked, test_site }.
export async function getProjectCustomerLink(projectId) {
  try { return await get(`/terraflow/projects/${projectId}/customer-link`); }
  catch { return null; }
}

// Per-OGF-object → TIB object links (keyed by ogf_object_ref = id_number),
// each with the object's inspections (type/status/assigned).
export async function getProjectObjectLinks(projectId) {
  try { const r = await get(`/terraflow/projects/${projectId}/object-links`); return r?.links || {}; }
  catch { return {}; }
}

// Create a TIB object under the project's linked test site from an OGF object,
// prefilling assessment info; links it to that OGF object row.
export async function createTibObjectFromProject(projectId, { ogfObjectRef, type, objectTag }) {
  return post(`/terraflow/projects/${projectId}/objects/create-tib`, {
    ogf_object_ref: ogfObjectRef, type: type || '', object_tag: objectTag || '',
  });
}

// Remove the link between an OGF object row and its TIB object (object kept).
export async function removeProjectObjectLink(projectId, ogfObjectRef) {
  return post(`/terraflow/projects/${projectId}/objects/remove-link`, { ogf_object_ref: ogfObjectRef });
}

// Existing TIB objects under the project's linked test site, each with the 3
// assessment fields used for the assign match check + already_linked_ref.
// Returns [] when the customer isn't linked to a test site yet.
export async function getProjectAssignableObjects(projectId) {
  try { const r = await get(`/terraflow/projects/${projectId}/assignable-objects`); return r?.objects || []; }
  catch { return []; }
}

// Link an existing TIB object to an OGF object row. The backend re-validates
// the 3-field assessment match and throws (422) on mismatch.
export async function linkExistingObjectToProject(projectId, { ogfObjectRef, objectId }) {
  return post(`/terraflow/projects/${projectId}/objects/link-existing`, {
    ogf_object_ref: ogfObjectRef, object_id: objectId,
  });
}

// Count of TerraFlow projects with no linked TIB object (red "pending" dot).
// Counted client-side off the existing projects list — no dedicated endpoint.
export function subscribeToTerraFlowProjectsPendingCount(callback) {
  return createPoller(
    async () => {
      const list = apiListToFrontend(await get('/terraflow/projects'));
      return list.filter((p) => !(Array.isArray(p.objectIds) && p.objectIds.length > 0)).length;
    },
    callback,
  );
}

// Count of TerraFlow projects with unacknowledged webhook changes (orange dot).
export function subscribeToTerraFlowProjectsChangeCount(callback) {
  return createPoller(
    async () => {
      const list = apiListToFrontend(await get('/terraflow/projects'));
      return list.filter((p) => Array.isArray(p.pendingChanges) && p.pendingChanges.length > 0).length;
    },
    callback,
  );
}

// Acknowledge (clear) a project's accumulated webhook changes.
export async function acknowledgeProjectChanges(projectId) {
  return post(`/terraflow/projects/${projectId}/acknowledge-changes`, {});
}

// Remove a project from the app entirely. Linked objects/inspections are kept
// (the links are removed first). Returns { status }.
export async function deleteTerraFlowProject(projectId) {
  return del(`/terraflow/projects/${projectId}`);
}

// Link an already-synced project to an existing TIB object (admin-only).
export async function linkTerraFlowProjectToObject(projectId, objectId) {
  return apiToFrontend(await put(`/terraflow/projects/${projectId}`, { object_id: objectId }));
}

// Create a TIB Object (and a Client if needed) from project data, in one
// atomic backend call. `payload` fields override the project's raw payload;
// omit any the user didn't edit. Returns { client, object } (camelCased).
export async function createObjectFromTerraFlowProject(projectId, payload = {}) {
  const body = {};
  if (payload.clientId    !== undefined) body.client_id    = payload.clientId;
  if (payload.clientName  !== undefined) body.client_name  = payload.clientName;
  if (payload.city        !== undefined) body.city         = payload.city;
  if (payload.type        !== undefined) body.type         = payload.type;
  if (payload.objectTag   !== undefined) body.object_tag   = payload.objectTag;
  if (payload.objectIndex !== undefined) body.object_index = payload.objectIndex;
  const data = await post(`/terraflow/projects/${projectId}/create-object`, body);
  return {
    client: data.client ? apiToFrontend(data.client) : null,
    object: data.object ? apiToFrontend(data.object) : null,
  };
}
